import { useCallback, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const query = (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
  return new URLSearchParams(cleaned).toString();
};

const detailUrl = (template, id) => template.replace(":id", id);

const useDiscipline = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const request = useCallback(
    async (method, url, data, successMessage) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          method,
          url,
          data,
          navigate,
        );
        if (response?.ok) {
          if (successMessage) message.success(successMessage);
          return response.data ?? true;
        }
        Utilities.showError(
          response,
          response?.data?.detail || response?.msg || "Request failed",
        );
        return null;
      } catch (error) {
        console.error(error);
        message.error("Request failed");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchCases = useCallback(
    async (params = {}) => {
      const qs = query(params);
      const data = await request(
        "GET",
        `${Settings.urls.academics.disciplineCases}${qs ? `?${qs}` : ""}`,
        null,
      );
      return listFrom(data);
    },
    [request],
  );

  const fetchCaseDetails = useCallback(
    (id) =>
      request(
        "GET",
        detailUrl(Settings.urls.academics.disciplineCaseDetail, id),
        null,
      ),
    [request],
  );
  const saveCase = useCallback(
    (data, id) =>
      request(
        id ? "PATCH" : "POST",
        id
          ? detailUrl(Settings.urls.academics.disciplineCaseDetail, id)
          : Settings.urls.academics.disciplineCases,
        data,
        id ? "Case updated" : "Case created",
      ),
    [request],
  );
  const submitCase = useCallback(
    (id, data = {}) =>
      request(
        "POST",
        detailUrl(Settings.urls.academics.disciplineCaseSubmit, id),
        data,
        "Case submitted",
      ),
    [request],
  );
  const sendBackCase = useCallback(
    (id, data) =>
      request(
        "POST",
        detailUrl(Settings.urls.academics.disciplineCaseSendBack, id),
        data,
        "Case sent back",
      ),
    [request],
  );
  const bulkEscalateCases = useCallback(
    (ids, toStatus, notes) =>
      request(
        "POST",
        Settings.urls.academics.disciplineCasesBulkEscalate,
        { ids, toStatus, notes },
        "Cases escalated",
      ),
    [request],
  );
  const bulkCloseCases = useCallback(
    async (ids, severities) => {
      setLoading(true);
      try {
        const body =
          severities && Object.keys(severities).length
            ? { ids, severities }
            : { ids };
        const response = await Utilities.fetchWithAuth(
          "POST",
          Settings.urls.academics.disciplineCasesBulkClose,
          body,
          navigate,
        );
        if (response?.ok) {
          message.success("Cases closed");
          return { ok: true, data: response.data ?? true };
        }
        const missing = response?.data?.missing_case_ids;
        if (Array.isArray(missing) && missing.length) {
          return { ok: false, missing };
        }
        Utilities.showError(
          response,
          response?.data?.detail || response?.msg || "Request failed",
        );
        return { ok: false };
      } catch (error) {
        console.error(error);
        message.error("Request failed");
        return { ok: false };
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const createEscalationStep = useCallback(
    (data) =>
      request(
        "POST",
        Settings.urls.academics.disciplineEscalationSteps,
        data,
        "Escalation step added",
      ),
    [request],
  );
  const updateEscalationStep = useCallback(
    (id, data) =>
      request(
        "PATCH",
        detailUrl(Settings.urls.academics.disciplineEscalationStepDetail, id),
        data,
        "Escalation step updated",
      ),
    [request],
  );
  const resolveEscalationStep = useCallback(
    (id, data) =>
      request(
        "POST",
        detailUrl(Settings.urls.academics.disciplineEscalationStepResolve, id),
        data,
        "Escalation step resolved",
      ),
    [request],
  );

  const fetchSeverityDeductions = useCallback(
    () =>
      request(
        "GET",
        Settings.urls.academics.disciplineSeverityDeductions,
        null,
      ),
    [request],
  );
  const saveSeverityDeductions = useCallback(
    (deductions) =>
      request(
        "PUT",
        Settings.urls.academics.disciplineSeverityDeductionsValidate,
        { deductions },
        "Severity deductions saved",
      ),
    [request],
  );

  const uploadEvidence = useCallback(
    (data) =>
      request(
        "POST",
        Settings.urls.academics.disciplineEvidence,
        data,
        "Evidence uploaded",
      ),
    [request],
  );
  const deleteEvidence = useCallback(
    (id) =>
      request(
        "DELETE",
        detailUrl(Settings.urls.academics.disciplineEvidenceDetail, id),
        null,
        "Evidence deleted",
      ),
    [request],
  );

  const createSanction = useCallback(
    (data) =>
      request(
        "POST",
        Settings.urls.academics.disciplineSanctions,
        data,
        "Sanction created",
      ),
    [request],
  );
  const updateSanction = useCallback(
    (id, data) =>
      request(
        "PATCH",
        detailUrl(Settings.urls.academics.disciplineSanctionDetail, id),
        data,
        "Sanction updated",
      ),
    [request],
  );
  const bulkApproveSanctions = useCallback(
    (ids, notes) =>
      request(
        "POST",
        Settings.urls.academics.disciplineSanctionsBulkApprove,
        { ids, notes },
        "Sanctions approved",
      ),
    [request],
  );
  const bulkLockSanctions = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.disciplineSanctionsBulkLock,
        { ids },
        "Sanctions locked",
      ),
    [request],
  );

  return {
    loading,
    fetchCases,
    fetchCaseDetails,
    saveCase,
    submitCase,
    sendBackCase,
    bulkEscalateCases,
    bulkCloseCases,
    createEscalationStep,
    updateEscalationStep,
    resolveEscalationStep,
    fetchSeverityDeductions,
    saveSeverityDeductions,
    uploadEvidence,
    deleteEvidence,
    createSanction,
    updateSanction,
    bulkApproveSanctions,
    bulkLockSanctions,
  };
};

export default useDiscipline;
